"use client";

import { rmcClient } from "@/lib/rmcClient";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

function fmtQty(v, digits = 3) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pick(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "—";
}

const PurchaseChallanPrint = forwardRef(function PurchaseChallanPrint({ detail }, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [freshDetail, setFreshDetail] = useState(null);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: detail?.challan_no || "purchase-challan",
  });

  const loadFreshDetail = async () => {
    if (!detail?.id) return null;
    const payload = (await rmcClient.getPurchaseChallan(detail.id)).data;
    setFreshDetail(payload);
    return payload;
  };

  useImperativeHandle(ref, () => ({
    openPrint: async () => {
      if (!detail) return;
      try {
        setIsPreparingPrint(true);
        await loadFreshDetail();
      } finally {
        setIsPreparingPrint(false);
      }
      printFn();
    },
  }));

  useEffect(() => {
    setFreshDetail(null);
  }, [detail?.id]);

  if (!detail) {
    return <div ref={contentRef} className="hidden" aria-hidden />;
  }

  const source = freshDetail || detail;
  const items = Array.isArray(source?.items) && source.items.length ? source.items : [source];
  const totalAmount = Number(source?.total_amount ?? source?.sub_total ?? source?.amount);
  const companyName = pick(company?.name, company?.company_name);
  const companyAddress = pick(company?.address, company?.address_line1);
  const companyPhone = pick(
    company?.contact_phone,
    company?.mobile_no,
    company?.mobile,
    company?.mobile_number,
    company?.phone,
    company?.contact_no
  );
  const companyGst = pick(company?.gst_number, company?.gstin);
  const companyBlessing = pick(company?.blessing, company?.invoice_blessing);

  const party = source?.party || source?.purchase_party || detail.party_details || {};
  const partyName = pick(party?.name, detail.party);
  const partyAddress = pick(
    party?.address,
    party?.billing_address,
    party?.address_line1,
    party?.location,
    source?.party_address
  );
  const partyPhone = pick(
    party?.phone,
    party?.contact_no,
    party?.mobile_no,
    party?.mobile,
    party?.mobile_number,
    source?.party_phone
  );
  const partyGst = pick(party?.gst_number, party?.gstin);
  const challanNo = pick(source?.challan_no, detail.challan_no);
  const challanDate = pick(source?.date, detail.date);
  const vehicleNo = pick(source?.vehicle_no, detail.vehicle_no);
  const uom = pick(source?.material?.uom, detail.uom);
  const remark = pick(source?.remark, detail.remark);
  const purchaseInvoiceNo = pick(source?.purchase_invoice_no, detail.purchase_invoice_no);
  const kata = source?.kata_details && typeof source.kata_details === "object" ? source.kata_details : null;
  const netWeightLbl = pick(source?.net_weight, detail.net_weight);

  const purchaseKindRaw = source?.purchase_kind ?? detail?.purchase_kind ?? "REGULAR";
  const isConcrete = String(purchaseKindRaw).toUpperCase() === "CONCRETE";
  const royaltyNo = pick(source?.royalty_no, detail?.royalty_no);
  const challanQuarryName = pick(
    source?.concrete_quarry?.name,
    source?.quarry?.name,
    detail?.concrete_quarry_name
  );
  const purchaseKindLabel =
    isConcrete ? "Concrete purchase" : "Regular purchase";

  function itemQuarryName(it) {
    return pick(it?.quarry?.name, it?.concrete_quarry?.name, it?.quarry_name);
  }

  function lineAmount(it) {
    const q = Number(it?.qty);
    const r = Number(it?.rate);
    const a = Number(it?.amount);
    if (Number.isFinite(a)) return a;
    if (Number.isFinite(q) && Number.isFinite(r)) return q * r;
    return NaN;
  }

  const Copy = ({ copyLabel }) => (
    <section className="h-[138.5mm] border border-slate-700 p-3 text-[10px] leading-tight">
      {companyBlessing !== "—" && (
        <div className="mb-2 text-center text-[10px] italic text-slate-700">
          {companyBlessing}
        </div>
      )}
      <div className="mb-2 flex items-start justify-between border-b border-slate-600 pb-2">
        <div>
          <p className="text-sm font-bold">{companyName}</p>
          <p className="max-w-[320px] text-[10px] text-slate-700">{companyAddress}</p>
          <p className="text-[10px] text-slate-700">
            GST: {companyGst} | Phone: {companyPhone}
          </p>
        </div>
        <div className="text-right">
          <p className="text-base font-bold tracking-wide">PURCHASE CHALLAN</p>
          <p className="mt-1 text-[10px] text-slate-700">{copyLabel}</p>
        </div>
      </div>

      <table className="mb-2 w-full border-collapse border border-slate-600">
        <tbody>
          <tr>
            <td className="w-28 border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Challan No</td>
            <td className="border border-slate-600 px-2 py-1">{challanNo}</td>
            <td className="w-24 border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Date</td>
            <td className="w-36 border border-slate-600 px-2 py-1">{challanDate}</td>
            <td className="w-24 border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Vehicle</td>
            <td className="w-36 border border-slate-600 px-2 py-1">{vehicleNo}</td>
          </tr>
          <tr>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Tax Type</td>
            <td className="border border-slate-600 px-2 py-1">{pick(source?.tax_type)}</td>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">UOM</td>
            <td className="border border-slate-600 px-2 py-1">{uom}</td>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Pur. inv. no.</td>
            <td className="border border-slate-600 px-2 py-1">{purchaseInvoiceNo}</td>
          </tr>
          <tr>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Purchase type</td>
            <td className="border border-slate-600 px-2 py-1" colSpan={5}>
              {purchaseKindLabel}
            </td>
          </tr>
          {isConcrete && (
            <tr>
              <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Royalty no.</td>
              <td className="border border-slate-600 px-2 py-1" colSpan={3}>
                {royaltyNo}
              </td>
              <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Quarry (challan)</td>
              <td className="border border-slate-600 px-2 py-1">{challanQuarryName}</td>
            </tr>
          )}
          <tr>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Gross / Tare</td>
            <td className="border border-slate-600 px-2 py-1" colSpan={3}>
              {kata
                ? `${fmtQty(kata.gross_weight)} / ${fmtQty(kata.tare_weight)} (auto net ${fmtQty(kata.auto_net_weight)})`
                : "—"}
            </td>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Net wt.</td>
            <td className="border border-slate-600 px-2 py-1">{fmtQty(netWeightLbl)}</td>
          </tr>
          <tr>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold" colSpan={2}>
              Remark
            </td>
            <td className="border border-slate-600 px-2 py-1" colSpan={4}>
              {remark}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="mb-2 w-full border-collapse border border-slate-600">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-600 px-2 py-1 text-left">Party Name</th>
            <th className="border border-slate-600 px-2 py-1 text-left">Party Address</th>
            <th className="border border-slate-600 px-2 py-1 text-left">Phone</th>
            <th className="border border-slate-600 px-2 py-1 text-left">GST</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-slate-600 px-2 py-1">{partyName}</td>
            <td className="border border-slate-600 px-2 py-1">{partyAddress}</td>
            <td className="border border-slate-600 px-2 py-1">{partyPhone}</td>
            <td className="border border-slate-600 px-2 py-1">{partyGst}</td>
          </tr>
        </tbody>
      </table>

      <table className="w-full border-collapse border border-slate-600">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-600 px-2 py-1 text-left">Description</th>
            {isConcrete && (
              <th className="border border-slate-600 px-2 py-1 text-left">Quarry</th>
            )}
            <th className="border border-slate-600 px-2 py-1 text-right">Quantity ({uom})</th>
            <th className="border border-slate-600 px-2 py-1 text-right">Rate</th>
            <th className="border border-slate-600 px-2 py-1 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={idx}>
              <td className="border border-slate-600 px-2 py-1">{pick(it?.material?.name, detail.material)}</td>
              {isConcrete && (
                <td className="border border-slate-600 px-2 py-1 text-[10px] leading-snug">
                  {itemQuarryName(it)}
                </td>
              )}
              <td className="border border-slate-600 px-2 py-1 text-right">
                {fmtQty(it?.qty ?? source?.net_weight ?? detail?.net_weight ?? detail?.qty)}
              </td>
              <td className="border border-slate-600 px-2 py-1 text-right">{fmtMoney(it?.rate)}</td>
              <td className="border border-slate-600 px-2 py-1 text-right">{fmtMoney(lineAmount(it))}</td>
            </tr>
          ))}
          <tr>
            <td
              className="border border-slate-600 px-2 py-1 text-right font-semibold"
              colSpan={isConcrete ? 4 : 3}
            >
              Total
            </td>
            <td className="border border-slate-600 px-2 py-1 text-right font-semibold">{fmtMoney(totalAmount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="h-12 border border-slate-600 p-2 text-[10px]">Received By</div>
        <div className="h-12 border border-slate-600 p-2 text-[10px]">Checked By</div>
        <div className="h-12 border border-slate-600 p-2 text-[10px]">Authorized Signature</div>
      </div>
    </section>
  );

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white text-black print:block">
        <div className="mx-auto h-[297mm] w-[210mm] p-3">
          <Copy copyLabel="ORIGINAL" />
          <Copy copyLabel="DUPLICATE" />
        </div>
      </div>
    </div>
  );
});

PurchaseChallanPrint.displayName = "PurchaseChallanPrint";

export default PurchaseChallanPrint;
